import { map, type Observable } from 'rxjs'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import {
  createSnackbarErrorHandlerFactory,
  createSnackbarRetryWrapperFactory,
  createSnackbarFactory,
  SnackbarsManager,
} from '@/features/shell/snackbars/SnackbarsManager'
import {
  type SnackbarFactory,
  type SnackbarHandle,
  type SnackbarType,
  type SnackbarErrorHandlerFactory,
  type SnackbarRetryWrapperFactory,
} from '@/features/shell/snackbars'
import { type Theme } from '@/features/shell/theming'
import { ThemeManager } from '@/features/shell/theming/ThemeManager'
import { ChromeThemeManager } from '@/features/shell/theming/ChromeThemeManager'
import { ShellInfo } from '@/features/shell/ShellInfo'
import type { FetchTermsAcceptanceInfoFn } from '@/features/account'
import { HandoffViewModel } from '@/features/shell/handoff/HandoffViewModel'
import type { SessionOptions } from '@/features/iam/auth/SessionOptions'
import { ExitMethodStore } from '@/features/shell/exiting/ExitMethodStore'
import type { NavigateFn } from '@/RootStore'
import type { ExitMethod } from '@/features/shell/exiting/ExitMethod'
import { NativePlatformDelegate } from '@/features/shell/platform/NativePlatformDelegate'
import { DefaultPlatformDelegate } from '@/features/shell/platform/DefaultPlatformDelegate'

export interface ShellFeatureOptions {
  /**
   * Delegate for navigating with the client-side router.
   */
  navigate: NavigateFn

  /**
   * Delegate for getting the current workspace
   */
  getCurrentWorkspace(): Workspace

  /**
   * Stream for the current workspace, which doesn't have the limitation of
   * requiring authentication unlike {@link getCurrentWorkspace}.
   */
  currentWorkspace$: Observable<Workspace | null>

  /**
   * An observable that emits a theme based on the selected workspace.
   */
  workspaceTheme$: Observable<Theme>

  /**
   * An observable that emits the session options.
   */
  sessionOptions$: Observable<SessionOptions>

  /**
   * Delegate for fetching terms acceptance information.
   */
  fetchTermsAcceptance: FetchTermsAcceptanceInfoFn
}

/**
 * Starts the handoff flow if applicable.
 * Will resolve early if handoff is not needed.
 *
 * Handoff is a "welcome" dialog specifically for B2B2C customers
 * that informs the user of the partnership between Taxfyle and the
 * business that sent them to us. The flow is only applicable for B2B2C
 * workspaces that use "external customers" and have magic links enabled.
 * If not all of those conditions are met, the returned promise will be resolved
 * immediately.
 *
 * The handoff experience is also only shown if the user has not already
 * accepted the latest terms.
 */
export type StartHandoffFn = (
  workspace: Workspace,
  signal?: AbortSignal,
) => Promise<void>

/**
 * Reactive getter for an exit method.
 */
export type GetExitMethodFn = () => ExitMethod

/**
 * Manages the surrounding application shell such as the navigation.
 */
export type ShellFeature = ReturnType<typeof ShellFeature>

export {
  type SnackbarFactory,
  type SnackbarHandle,
  type SnackbarType,
  type SnackbarErrorHandlerFactory,
  type SnackbarRetryWrapperFactory,
}

export function ShellFeature(options: ShellFeatureOptions) {
  const snackbarsManager = new SnackbarsManager()

  const themeManager = new ThemeManager(options.workspaceTheme$)
  themeManager.activate()

  const chromeThemeManager = new ChromeThemeManager()

  const exitMethodStore = new ExitMethodStore(
    options.navigate,
    options.sessionOptions$,
  )

  const getRootExitMethod: GetExitMethodFn = () =>
    exitMethodStore.rootExitMethod
  const getPageExitMethod: GetExitMethodFn = () =>
    exitMethodStore.pageExitMethod
  const rootExitMethod$ = exitMethodStore.exitMethods$.pipe(
    map(({ rootExitMethod }) => rootExitMethod),
  )
  const pageExitMethod$ = exitMethodStore.exitMethods$.pipe(
    map(({ pageExitMethod }) => pageExitMethod),
  )

  const newSnackbar = createSnackbarFactory(snackbarsManager)
  const createSnackbarErrorHandler =
    createSnackbarErrorHandlerFactory(snackbarsManager)
  const createSnackbarRetryWrapper =
    createSnackbarRetryWrapperFactory(snackbarsManager)

  const platformDelegate = NativePlatformDelegate.isSupported
    ? NativePlatformDelegate.instance
    : DefaultPlatformDelegate.instance

  const shellInfo = ShellInfo(() => {
    const workspace = options.getCurrentWorkspace()
    return {
      name: workspace.name,
      iconUrl: workspace.branding.logos.icon,
      invertedIconUrl: workspace.branding.logos.iconWhite,
      logoUrl: workspace.branding.logos.primary,
      invertedLogoUrl: workspace.branding.logos.primaryWhite,
      systemRobotAvatarUrl: workspace.branding.logos.systemRobotAvatar,
      showTaxfyleInfo: workspace.classification === 'B2C',
    }
  })

  const handoffVm = new HandoffViewModel(
    options.fetchTermsAcceptance,
    getRootExitMethod,
  )

  const startHandoff: StartHandoffFn = (workspace, signal) =>
    handoffVm.startHandoff(workspace, signal)

  return {
    // Internal
    internal: {
      // We're not lazy-loading the internals because the shell
      // is used everywhere.
      handoffVm,
      snackbarsManager,
      themeManager,
      chromeThemeManager,
      exitMethodStore,
      platformDelegate,
    },

    // Public
    shellInfo,
    getExternalUrlDelegate: platformDelegate.getExternalUrlDelegate,
    getDocumentDownloadDelegate: platformDelegate.getDocumentDownloadDelegate,
    getAddToCalendarDelegate: platformDelegate.getAddToCalendarDelegate,
    createSnackbarErrorHandler,
    createSnackbarRetryWrapper,
    newSnackbar,
    startHandoff,
    theme$: themeManager.theme$,
    colorScheme$: themeManager.colorScheme$,
    sessionOptions$: options.sessionOptions$,
    getRootExitMethod,
    getPageExitMethod,
    rootExitMethod$,
    pageExitMethod$,
  }
}
